'use client';

import { useEffect, useRef } from 'react';
import type { SiteTradingViewWidget } from '@/server.api/types';

type TradingViewTickerTapeProps = {
    scriptUrl: string;
    loadingLabel: string;
    widget: SiteTradingViewWidget;
};

export default function TradingViewTickerTape({
    scriptUrl,
    loadingLabel,
    widget,
}: TradingViewTickerTapeProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.inert = true;
        container.style.setProperty('--market-ticker-loading-background', widget.colors.background);

        const originalAttachShadow = Element.prototype.attachShadow;
        let shadowPatchActive = true;
        function patchedAttachShadow(this: Element, init: ShadowRootInit) {
            return originalAttachShadow.call(
                this,
                this.localName.startsWith('tv-') ? { ...init, mode: 'open' } : init,
            );
        }
        const restoreAttachShadow = () => {
            if (!shadowPatchActive) return;

            if (Element.prototype.attachShadow === patchedAttachShadow) {
                Element.prototype.attachShadow = originalAttachShadow;
            }
            shadowPatchActive = false;
        };

        Element.prototype.attachShadow = patchedAttachShadow;
        const shadowPatchTimeout = window.setTimeout(restoreAttachShadow, 45000);

        const loading = document.createElement('p');
        loading.className = 'market-ticker__loading';
        loading.setAttribute('role', 'status');
        loading.textContent = loadingLabel;

        const track = document.createElement('div');
        track.className = 'market-ticker__track';

        const symbols = widget.tabs.flatMap((tab) => tab.symbols);
        let itemWidths: number[] = [];

        const applyItemWidths = (group: HTMLElement) => {
            Array.from(group.children).forEach((item, index) => {
                const width = itemWidths[index];
                if (width) (item as HTMLElement).style.width = `${width}px`;
            });
        };

        const createGroup = (hidden = false) => {
            const group = document.createElement('div');
            group.className = 'market-ticker__group';

            if (hidden) {
                group.setAttribute('aria-hidden', 'true');
            }

            symbols.forEach((symbol) => {
                const item = document.createElement('span');
                item.className = 'market-ticker__item';

                const ticker = document.createElement('tv-single-ticker');
                ticker.setAttribute('symbol', symbol);
                if (widget.hideMarketStatus) {
                    ticker.setAttribute('hide-market-status', '');
                }
                ticker.setAttribute('theme', widget.theme);
                ticker.setAttribute('transparent', '');
                ticker.setAttribute('lang', widget.locale);

                item.append(ticker);
                group.append(item);
            });

            applyItemWidths(group);
            return group;
        };

        const primaryGroup = createGroup();
        track.append(primaryGroup, createGroup(true));

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.type = 'module';
        script.async = true;

        container.replaceChildren(loading, track, script);

        let active = true;
        let componentDefined = false;
        let starting = false;
        let running = false;
        let loopWidth = 0;
        let startTimer = 0;
        let revealTimer = 0;
        let layoutFrame = 0;
        let mutationFrame = 0;
        let revealFrame = 0;

        const shadowText = (root: ParentNode): string => {
            let text = root.textContent ?? '';

            root.querySelectorAll('*').forEach((element) => {
                if (element.shadowRoot) text += ` ${shadowText(element.shadowRoot)}`;
            });

            return text;
        };

        const quotesAreReady = () =>
            Array.from(primaryGroup.querySelectorAll('tv-single-ticker')).every((ticker) => {
                const text = ticker.shadowRoot ? shadowText(ticker.shadowRoot) : '';
                return /\d/.test(text) && text.includes('%');
            });

        const syncGroupCopies = () => {
            if (loopWidth < 1) return;

            const groupCount = Math.max(2, Math.ceil(container.clientWidth / loopWidth) + 1);
            while (track.childElementCount < groupCount) {
                track.append(createGroup(true));
            }
            while (track.childElementCount > groupCount) {
                track.lastElementChild?.remove();
            }
        };

        const freezeTrackGeometry = () => {
            if (!active || running || starting) return;

            const primaryItems = Array.from(
                primaryGroup.querySelectorAll<HTMLElement>(':scope > .market-ticker__item'),
            );
            if (primaryItems.length !== symbols.length) return;

            const measuredWidths = primaryItems.map((item) =>
                Math.ceil(item.getBoundingClientRect().width),
            );
            if (measuredWidths.some((width) => width < 1)) return;

            starting = true;
            itemWidths = measuredWidths.map((width) => width + 2);
            Array.from(track.children).forEach((group) => applyItemWidths(group as HTMLElement));

            layoutFrame = requestAnimationFrame(() => {
                if (!active) return;

                loopWidth = Math.ceil(primaryGroup.getBoundingClientRect().width);
                if (loopWidth < 1) {
                    starting = false;
                    return;
                }

                track.style.setProperty('--market-ticker-offset', `-${loopWidth}px`);
                track.style.setProperty(
                    '--market-ticker-duration',
                    `${Math.max(18, loopWidth / 80)}s`,
                );

                running = true;
                syncGroupCopies();
                track.classList.add('market-ticker__track--running');
                stabilityObserver.disconnect();

                revealTimer = window.setTimeout(() => {
                    queueEmbeddedTree(container);
                    revealFrame = requestAnimationFrame(() => {
                        revealFrame = requestAnimationFrame(() => {
                            if (!active) return;

                            prepareEmbeddedTree(container);
                            restoreAttachShadow();
                            loading.classList.add('market-ticker__loading--hidden');
                        });
                    });
                }, 800);
            });
        };

        const scheduleStart = (restartForResize = false) => {
            if (!active || running || starting || !componentDefined || !quotesAreReady()) {
                return;
            }

            if (restartForResize && startTimer) {
                window.clearTimeout(startTimer);
                startTimer = 0;
            }
            if (startTimer) return;

            startTimer = window.setTimeout(() => {
                startTimer = 0;
                freezeTrackGeometry();
            }, 400);
        };

        const observedRoots = new Set<Node>();
        const pendingRoots = new Set<ParentNode>();

        const prepareEmbeddedTree = (root: ParentNode) => {
            if (!widget.allowAttributionRemoval) return;

            if (
                root instanceof ShadowRoot &&
                root.host.localName === 'tv-attribution-boundary' &&
                !root.querySelector('style[data-news-site-unbranded]')
            ) {
                const unbrandedStyles = document.createElement('style');
                unbrandedStyles.setAttribute('data-news-site-unbranded', '');
                unbrandedStyles.textContent =
                    '.bottom-link{display:none!important}.border{border-color:transparent!important}';
                root.append(unbrandedStyles);
            }

            root.querySelectorAll(
                '[slot="branding"], .bottom-link, .tradingview-widget-copyright, tv-attribution-logo, a[href*="tradingview.com"], img[alt*="TradingView"]',
            ).forEach((element) => element.remove());

            root.querySelectorAll('tv-attribution-boundary').forEach((element) => {
                element.setAttribute('borderless', '');
                element.setAttribute('no-link', '');
            });

            root.querySelectorAll('iframe, a').forEach((element) => {
                element.setAttribute('tabindex', '-1');
            });

            root.querySelectorAll('*').forEach((element) => {
                const shadowRoot = element.shadowRoot;
                if (!shadowRoot || observedRoots.has(shadowRoot)) return;

                observedRoots.add(shadowRoot);
                observer.observe(shadowRoot, { childList: true, subtree: true });
                prepareEmbeddedTree(shadowRoot);
            });
        };

        const queueEmbeddedTree = (root: ParentNode) => {
            pendingRoots.add(root);
            if (mutationFrame) return;

            mutationFrame = requestAnimationFrame(() => {
                mutationFrame = 0;
                pendingRoots.forEach((pendingRoot) => prepareEmbeddedTree(pendingRoot));
                pendingRoots.clear();
            });
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationRoot = mutation.target.getRootNode();
                if (mutationRoot instanceof Document || mutationRoot instanceof ShadowRoot) {
                    queueEmbeddedTree(mutationRoot);
                }

                mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element) queueEmbeddedTree(node);
                });
            });

            scheduleStart();
        });
        observer.observe(container, { childList: true, subtree: true });
        observedRoots.add(container);
        queueEmbeddedTree(container);

        const stabilityObserver = new ResizeObserver(() => scheduleStart(true));
        stabilityObserver.observe(primaryGroup);

        const viewportObserver = new ResizeObserver(() => {
            if (running) syncGroupCopies();
        });
        viewportObserver.observe(container);

        customElements.whenDefined('tv-single-ticker').then(() => {
            if (!active) return;

            componentDefined = true;
            scheduleStart(true);
        });

        return () => {
            active = false;
            window.clearTimeout(shadowPatchTimeout);
            window.clearTimeout(startTimer);
            window.clearTimeout(revealTimer);
            restoreAttachShadow();
            cancelAnimationFrame(layoutFrame);
            cancelAnimationFrame(mutationFrame);
            cancelAnimationFrame(revealFrame);
            stabilityObserver.disconnect();
            viewportObserver.disconnect();
            observer.disconnect();
            container.style.removeProperty('--market-ticker-loading-background');
            container.replaceChildren();
        };
    }, [loadingLabel, scriptUrl, widget]);

    return (
        <aside className='market-ticker' aria-label={widget.ariaLabel}>
            <div
                ref={containerRef}
                className={`market-ticker__widget${widget.allowAttributionRemoval ? ' market-ticker__widget--unbranded' : ''}`}
            >
                <p className='market-ticker__loading' role='status'>
                    {loadingLabel}
                </p>
            </div>
        </aside>
    );
}
